/**
 * Utility class for text formatting operations
 */
export class TextFormatters {
  /**
   * Capitalizes the first letter of the text and makes the rest lowercase
   * @param text - The text to capitalize
   * @returns Capitalized text or the original value if null/undefined
   */
  static capitalize(text: string): string {
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  /**
   * Converts the entire text to uppercase
   * @param text - The text to convert
   * @returns Uppercase text or the original value if null/undefined
   */
  static uppercase(text: string): string {
    if (!text) return text;
    return text.toUpperCase();
  }

  /**
   * Formats a date object to API string format (YYYY-MM-DD)
   * @param date - The date to format
   * @returns Formatted date string or null if date is null/invalid
   */
  static formatDateForAPI(date: Date | string | null): string | null {
    if (!date) return null;

    if (typeof date === 'string') return date;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  /**
   * Calculates the age in years from a birth date string
   * @param birthDate - The birth date string
   * @returns Age in years
   */
  static checkAge(birthDate: string): number {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  }

  /**
   * Parses an API date string (YYYY-MM-DD) to a Date object
   * @param dateString - The date string to parse
   * @returns Date object or null if dateString is null/invalid
   */
  static parseDateFromAPI(dateString: string | null): Date | null {
    if (!dateString) return null;

    if (
      typeof dateString === 'string' &&
      dateString.match(/^\d{4}-\d{2}-\d{2}$/)
    ) {
      const [year, month, day] = dateString
        .split('-')
        .map(num => parseInt(num, 10));
      return new Date(year, month - 1, day);
    }

    return new Date(dateString);
  }
}
